/**
 * Modelo del disenador de plantillas bancarias (contexto bank-file del
 * modulo de payroll).
 *
 * La plantilla se define en YAML (el usuario SI edita el YAML, con un editor
 * asistido). Cada plantilla tiene como maximo UNA version publicada visible
 * (V1, V2, ...) y como maximo UNA version de trabajo (borrador) en curso.
 *
 * El front NO valida ni interpreta el YAML: la validacion es del backend
 * (POST /validate) y la vista previa la genera el backend (POST /:id/dry-run).
 */

/* Opcion simple para selects (mismo shape que usa DSelectField) */
export interface SelectOption {
  id: string
  name: string
}

/* Banco del catalogo real (pg_banks) */
export interface BankOption extends SelectOption {
  code: string
}

/* Fila del listado de plantillas */
export interface BankTemplateSummary {
  id: string
  name: string
  bankName: string
  countryId: string
  country: string
  /* Version publicada (V1, V2, ...); null si nunca se ha publicado */
  publishedVersion: number | null
  /* true si hay un borrador (version de trabajo) en curso */
  hasDraft: boolean
  /* false = plantilla inactivada por completo */
  active: boolean
  updatedAt: string
}

/* Plantilla completa: version publicada + version de trabajo (borrador) */
export interface BankTemplate {
  id: string
  name: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  active: boolean
  publishedVersion: number | null
  /* YAML de la version de trabajo; null cuando no hay borrador */
  draftYaml: string | null
  /* YAML visible en modo lectura (ultima version publicada) */
  publishedYaml: string | null
  updatedAt: string
}

/* ------------------------------------------------------------------------- */
/* Validacion / Dry Run (views del backend, normalizados en adapters.ts)      */
/* ------------------------------------------------------------------------- */

export interface ValidationIssue {
  severity: 'error' | 'warning'
  message: string
  /* Linea del YAML (1-indexed) cuando el backend la reporta */
  line?: number
}

export interface ValidationResult {
  valid: boolean
  issues: ValidationIssue[]
}

/* Resultado del dry run generado por el backend con datos sinteticos */
export interface DryRunResult {
  fileName: string
  format: string
  extension: string
  /* 'utf-8' para formatos de texto; 'base64' para binarios (EXCEL) */
  encoding: string
  /* Archivo completo tal como lo genero el backend */
  content: string
  warnings: string[]
  /* Lineas derivadas de content, para el preview de formatos de texto */
  lines: string[]
}

/* Errores de dominio del backend que traen issues de la plantilla */
export class BankTemplateApiError extends Error {
  readonly issues: ValidationIssue[]
  /* Codigo de dominio del backend (p. ej. BankTemplateNotFound) para i18n */
  readonly code?: string
  readonly params?: Record<string, unknown>

  constructor(
    message: string,
    issues: ValidationIssue[] = [],
    code?: string,
    params?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'BankTemplateApiError'
    this.issues = issues
    this.code = code
    this.params = params
  }
}

/* ------------------------------------------------------------------------- */
/* Soporte del editor (GET /editor-support, con fallback estatico)            */
/* ------------------------------------------------------------------------- */

/* Sugerencia de autocompletado dentro de {{...}} */
export interface CanonicalSuggestion {
  /* Ruta (o funcion) que se inserta */
  value: string
  /* Pista corta mostrada a la derecha en el dropdown */
  hint: string
}

export interface EditorSupport {
  suggestions: CanonicalSuggestion[]
}

/* ------------------------------------------------------------------------- */
/* Documentacion para crear plantillas bancarias                             */
/* ------------------------------------------------------------------------- */

export interface BankTemplateDocumentationSection {
  id: string
  title: string
  fileName: string
  contentType: string
  content: string
}

export interface BankTemplateDocumentationExample {
  id: string
  title: string
  fileName: string
  contentType: string
  content: string
}

export interface BankTemplateDocumentation {
  title: string
  sections: BankTemplateDocumentationSection[]
  examples: BankTemplateDocumentationExample[]
}

/**
 * Fallback local cuando GET /editor-support no responde. Refleja el canonical
 * model y las funciones inline del generador ({{digitsOnly(...)}}, ...).
 */
export const FALLBACK_SUGGESTIONS: CanonicalSuggestion[] = [
  { value: 'version', hint: 'string' },
  { value: 'request.options.templateId', hint: 'string' },
  { value: 'request.options.reference', hint: 'string' },
  { value: 'request.options.paymentDate', hint: 'date' },
  { value: 'request.options.description', hint: 'string' },
  { value: 'request.filters.startDate', hint: 'date' },
  { value: 'request.filters.endDate', hint: 'date' },
  { value: 'company.code', hint: 'string' },
  { value: 'company.name', hint: 'string' },
  { value: 'company.fiscalId', hint: 'string' },
  { value: 'company.address', hint: 'string' },
  { value: 'company.phone', hint: 'string' },
  { value: 'company.email', hint: 'string' },
  { value: 'company.account.number', hint: 'string' },
  { value: 'company.account.iban', hint: 'string' },
  { value: 'company.account.clientNumber', hint: 'string' },
  { value: 'company.account.bank.code', hint: 'string' },
  { value: 'company.account.bank.name', hint: 'string' },
  { value: 'company.account.bank.type.code', hint: 'string' },
  { value: 'company.account.bank.type.name', hint: 'string' },
  { value: 'company.account.bank.parentCode', hint: 'string | null' },
  { value: 'company.account.bank.country.code', hint: 'string' },
  { value: 'company.account.bank.country.name', hint: 'string' },
  { value: 'company.account.bank.branch.code', hint: 'string' },
  { value: 'company.account.bank.branch.name', hint: 'string' },
  { value: 'company.account.currency.code', hint: 'string' },
  { value: 'company.account.currency.numericCode', hint: 'string' },
  { value: 'company.account.currency.symbol', hint: 'string' },
  { value: 'company.account.currency.decimals', hint: 'integer' },
  { value: 'payroll.code', hint: 'string' },
  { value: 'payroll.name', hint: 'string' },
  { value: 'payroll.process.code', hint: 'string' },
  { value: 'payroll.process.name', hint: 'string' },
  { value: 'payroll.period.startDate', hint: 'date' },
  { value: 'payroll.period.endDate', hint: 'date' },
  { value: 'payroll.currency.code', hint: 'string' },
  { value: 'payroll.currency.numericCode', hint: 'string' },
  { value: 'payroll.currency.symbol', hint: 'string' },
  { value: 'payroll.currency.decimals', hint: 'integer' },
  { value: 'payroll.closedAt', hint: 'datetime' },
  { value: 'transaction.sequence', hint: 'integer' },
  { value: 'transaction.beneficiary.documentNumber', hint: 'string' },
  { value: 'transaction.beneficiary.documenType', hint: 'string' },
  { value: 'transaction.beneficiary.fiscalId', hint: 'string' },
  { value: 'transaction.beneficiary.firstName', hint: 'string' },
  { value: 'transaction.beneficiary.middleName', hint: 'string' },
  { value: 'transaction.beneficiary.lastName', hint: 'string' },
  { value: 'transaction.beneficiary.secondLastName', hint: 'string' },
  { value: 'transaction.beneficiary.fullName', hint: 'string' },
  { value: 'transaction.beneficiary.workEmail', hint: 'string' },
  { value: 'transaction.beneficiary.personalEmail', hint: 'string' },
  { value: 'transaction.beneficiary.phone', hint: 'string' },
  { value: 'transaction.beneficiary.address', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.number', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.code', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.name', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.type.code', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.type.name', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.branch.code', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.bank.branch.name', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.type.code', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.type.name', hint: 'string' },
  { value: 'transaction.beneficiaryAccount.currency.code', hint: 'string' },
  {
    value: 'transaction.beneficiaryAccount.currency.numericCode',
    hint: 'string'
  },
  { value: 'transaction.beneficiaryAccount.currency.symbol', hint: 'string' },
  {
    value: 'transaction.beneficiaryAccount.currency.decimals',
    hint: 'integer'
  },
  { value: 'transaction.amount', hint: 'decimal (string)' },
  { value: 'totals.transactions', hint: 'integer' },
  { value: 'totals.amount', hint: 'decimal (string)' },
  { value: 'output.extension', hint: 'string' },
  { value: 'digitsOnly(', hint: 'digitsOnly(valor)' },
  { value: 'upper(', hint: 'upper(valor)' },
  { value: 'lower(', hint: 'lower(valor)' },
  { value: 'trim(', hint: 'trim(valor)' },
  { value: 'removeAccents(', hint: 'removeAccents(valor)' },
  { value: 'fixedAmount(', hint: 'fixedAmount(monto, decimales, conPunto)' }
]
